import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import websocketService from '../services/websocket';
import routes from '../utils/routes';
import { clearAuth } from '../utils/auth';

/**
 * Custom hook to manage WebSocket connection state and events
 * 
 * @returns {Object} WebSocket connection state and utility functions
 */
const useWebSocketConnection = () => {
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState(websocketService.getConnectionState());
  const [authStatus, setAuthStatus] = useState(websocketService.isAuthenticated() ? 'authenticated' : 'unauthenticated');
  const [error, setError] = useState(null);
  
  // Use refs to prevent state update loops
  const connectionStatusRef = useRef(connectionStatus);
  const authStatusRef = useRef(authStatus);
  const errorRef = useRef(error);
  
  // Update refs when state changes
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);
  
  useEffect(() => {
    authStatusRef.current = authStatus;
  }, [authStatus]);
  
  useEffect(() => {
    errorRef.current = error;
  }, [error]);
  
  // Set up WebSocket connection and event listeners
  useEffect(() => {
    let isMounted = true;
    
    // console.log('[useWebSocketConnection] Setting up WebSocket connection and listeners');
    
    // Immediately attempt connection when component mounts
    const initConnection = async () => {
      try {
        // console.log('[useWebSocketConnection] Initiating WebSocket connection');
        await websocketService.connect();
        // console.log('[useWebSocketConnection] WebSocket connection initiated successfully');
      } catch (error) {
        // console.error('[useWebSocketConnection] Error initiating WebSocket connection:', error);
        if (isMounted) {
          setError('Failed to establish WebSocket connection. Please try again.');
        }
      }
    };
    
    // Start the connection
    initConnection();
    
    // Set up event listeners
    const handleConnecting = () => {
      if (isMounted && connectionStatusRef.current !== 'connecting') {
        // console.log('[useWebSocketConnection] WebSocket connecting');
        setConnectionStatus('connecting');
      }
    };
    
    const handleConnect = () => {
      if (isMounted && connectionStatusRef.current !== 'connected') {
        // console.log('[useWebSocketConnection] WebSocket connected');
        setConnectionStatus('connected');
        if (errorRef.current) setError(null);
      }
    };

    const handleDisconnect = (data) => {
      if (!isMounted) return;
      
      // console.log('[useWebSocketConnection] WebSocket disconnected:', data);
      
      if (connectionStatusRef.current !== 'disconnected') {
        setConnectionStatus('disconnected');
      }
      
      if (authStatusRef.current !== 'unauthenticated') {
        setAuthStatus('unauthenticated');
      }
      
      // Provide more detailed error information for certain close codes
      if (data && data.code) {
        if (data.code === 1006) {
          setError('Connection closed abnormally. The server might be down or there might be network issues.');
        } else if (data.code === 1015) {
          setError('TLS handshake failed. There might be SSL certificate issues.');
        }
      }
    };

    const handleError = (error) => {
      if (!isMounted) return;
      
      // console.error('[useWebSocketConnection] WebSocket error:', error);
      
      // Check for specific error types to provide better error messages
      if (error && typeof error === 'object') {
        // Handle timeout errors for specific data fetches
        if (error.type === 'error' && error.data && typeof error.data === 'string') {
          // Check for timeout errors related to price change data
          if (error.data.includes('Failed to get 24-hour price change data: timeout')) {
            setError('Timeout while retrieving price change data. This is a temporary issue and doesn\'t affect your connection.');
            return; // Don't update connection status for data-specific timeouts
          }
          
          // Check for other timeout errors
          if (error.data.includes('timeout')) {
            setError(`Data fetch timeout: ${error.data}. This is a temporary issue.`);
            return; // Don't update connection status for data-specific timeouts
          }
          
          // Set more specific error for other types of data errors
          setError(error.data);
          return;
        }
      }
      
      // Default generic error message
      setError('WebSocket connection error. Please check your connection.');
      
      if (connectionStatusRef.current !== 'disconnected') {
        setConnectionStatus('disconnected');
      }
      
      if (authStatusRef.current !== 'unauthenticated') {
        setAuthStatus('unauthenticated');
      }
    };

    const handleAuthenticated = (data) => {
      if (!isMounted) return;
      
      // console.log('[useWebSocketConnection] WebSocket authenticated:', data);
      
      if (authStatusRef.current !== 'authenticated') {
        setAuthStatus('authenticated');
      }
      
      if (errorRef.current) {
        setError(null);
      }
    };

    const handleAuthError = (data) => {
      if (!isMounted) return;
      
      // console.error('[useWebSocketConnection] WebSocket authentication error:', data);
      
      if (authStatusRef.current !== 'auth_failed') {
        setAuthStatus('auth_failed');
      }
      
      // Set the error message
      const errorMessage = data.message || 'Unknown authentication error';
      setError(`WebSocket authentication failed: ${errorMessage}`);
      
      // Force disconnect to prevent retry attempts
      websocketService.forceDisconnect();
      
      // For critical auth errors, redirect to login immediately
      if (data.code === 'no_token' || 
          data.code === 'invalid_token_format' || 
          data.code === 'policy_violation' ||
          data.code === 'invalid_token' || 
          data.code === 'expired_token') {
        
        // console.log('[useWebSocketConnection] Critical auth error, redirecting to login');
        
        // Clear all auth data including tokens before redirecting
        clearAuth().then(() => {
          // console.log('[useWebSocketConnection] Auth data cleared successfully');
          
          // Set a flag in localStorage to prevent reconnection attempts
          localStorage.setItem('auth_redirect_triggered', 'true');
          
          // Get the login path from routes
          const loginPath = routes.LOGIN || '/login';
          
          // Use a short timeout to allow React state updates to complete
          setTimeout(() => {
            // Use direct window location change instead of React Router navigation
            // This forces a full page reload and avoids potential SPA routing issues
            window.location.href = loginPath;
          }, 100);
        }).catch(err => {
          // console.error('[useWebSocketConnection] Error clearing auth data:', err);
          // Redirect anyway as a fallback
          window.location.href = routes.LOGIN || '/login';
        });
      }
    };

    const handleMaxRetriesReached = () => {
      if (!isMounted) return;
      
      // console.error('[useWebSocketConnection] WebSocket max retries reached');
      
      if (connectionStatusRef.current !== 'max_retries') {
        setConnectionStatus('max_retries');
      }
      
      setError('Maximum reconnection attempts reached. The server may be down or there might be network issues. Please try again later or check your connection.');
      
      // Set a flag to prevent automatic reconnection attempts
      localStorage.setItem('ws_max_retries_reached', 'true');
      
      // If we've already tried to connect multiple times, do a hard refresh of the dashboard
      // to clear any potential stale state
      const reconnectAttempts = parseInt(localStorage.getItem('reconnect_attempts') || '0', 10);
      if (reconnectAttempts > 2) {
        // console.log('[useWebSocketConnection] Multiple reconnect attempts failed, forcing page reload');
        localStorage.removeItem('reconnect_attempts');
        // Use a short timeout to allow React state updates to complete
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        localStorage.setItem('reconnect_attempts', (reconnectAttempts + 1).toString());
      }
    };

    // Subscribe to WebSocket events
    websocketService.on('connecting', handleConnecting);
    websocketService.on('connect', handleConnect);
    websocketService.on('disconnect', handleDisconnect);
    websocketService.on('error', handleError);
    websocketService.on('authenticated', handleAuthenticated);
    websocketService.on('authentication_error', handleAuthError);
    websocketService.on('max_retries_reached', handleMaxRetriesReached);

    // Check initial connection state
    const initialConnectionState = websocketService.getConnectionState();
    // console.log('[useWebSocketConnection] Initial connection state:', initialConnectionState);
    
    if (initialConnectionState === 'connected') {
      setConnectionStatus('connected');
      if (websocketService.isAuthenticated()) {
        setAuthStatus('authenticated');
      }
    }

    // Cleanup function
    return () => {
      // console.log('[useWebSocketConnection] Cleaning up WebSocket listeners');
      isMounted = false;
      
      // Clean up event listeners
      websocketService.off('connecting', handleConnecting);
      websocketService.off('connect', handleConnect);
      websocketService.off('disconnect', handleDisconnect);
      websocketService.off('error', handleError);
      websocketService.off('authenticated', handleAuthenticated);
      websocketService.off('authentication_error', handleAuthError);
      websocketService.off('max_retries_reached', handleMaxRetriesReached);
      
      try {
        // Only disconnect if we're not rate limited to prevent connection cycling
        if (!localStorage.getItem('auth_rate_limited')) {
          // console.log('[useWebSocketConnection] Releasing WebSocket reference');
          websocketService.disconnect();
        } else {
          // console.log('[useWebSocketConnection] Rate limited - not disconnecting WebSocket to prevent reconnection cycles');
        }
      } catch (error) {
        // console.error('[useWebSocketConnection] Error during WebSocket disconnect in cleanup:', error);
      }
    };
  }, [navigate]);

  // Helper functions for WebSocket connection management
  const connect = async () => {
    try {
      // console.log('[useWebSocketConnection] Connecting to WebSocket...');
      await websocketService.connect();
      // console.log('[useWebSocketConnection] Successfully connected to WebSocket');
      return true;
    } catch (error) {
      // console.error('[useWebSocketConnection] Failed to connect to WebSocket:', error);
      setError('Failed to connect to WebSocket server');
      return false;
    }
  };

  const forceConnect = async () => {
    try {
      // console.log('[useWebSocketConnection] Force connecting to WebSocket...');
      await websocketService.forceConnect();
      // console.log('[useWebSocketConnection] Successfully force connected to WebSocket');
      return true;
    } catch (error) {
      // console.error('[useWebSocketConnection] Failed to force connect to WebSocket:', error);
      setError('Failed to connect to WebSocket server');
      return false;
    }
  };

  const disconnect = () => {
    try {
      // console.log('[useWebSocketConnection] Disconnecting from WebSocket...');
      websocketService.disconnect();
      // console.log('[useWebSocketConnection] Successfully disconnected from WebSocket');
      return true;
    } catch (error) {
      // console.error('[useWebSocketConnection] Failed to disconnect from WebSocket:', error);
      return false;
    }
  };

  const forceDisconnect = () => {
    try {
      // console.log('[useWebSocketConnection] Force disconnecting from WebSocket...');
      websocketService.forceDisconnect();
      // console.log('[useWebSocketConnection] Successfully force disconnected from WebSocket');
      return true;
    } catch (error) {
      // console.error('[useWebSocketConnection] Failed to force disconnect from WebSocket:', error);
      return false;
    }
  };

  return {
    connectionStatus,
    authStatus,
    error,
    setError,
    connect,
    forceConnect,
    disconnect,
    forceDisconnect
  };
};

export default useWebSocketConnection; 